import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../App";
import { callApi, CDN_URL, formatDate, getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";
import {
  MdAdd,
  MdArrowForward,
  MdDelete,
  MdEdit,
  MdForum,
  MdLockOutline,
  MdSearch,
} from "react-icons/md";
import { Editor } from "@tinymce/tinymce-react";

function stripHtml(text = "") {
  return String(text || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function shortenText(text, limit = 180) {
  const cleanText = stripHtml(text);
  if (cleanText.length <= limit) {
    return cleanText;
  }

  return `${cleanText.slice(0, limit).trim()}...`;
}

function hasMeaningfulContent(text = "") {
  return stripHtml(text).length > 0;
}

export default function ForumHome() {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [filter, setFilter] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingThread, setEditingThread] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const createEditorRef = useRef(null);

  const canPost =
    appContext.auth &&
    String(appContext.currentUserData?.status || "")
      .trim()
      .toLowerCase() === "registered";

  async function init() {
    setReady(false);
    await appContext.getAllThreads();
    setReady(true);
  }

  async function imageUploadHandler(blobInfo) {
    return new Promise(async (resolve, reject) => {
      await callApi("upload-attachment", { file: blobInfo.blob() }).then(
        (response) => {
          if (response.status === 1 && response.files?.file) {
            resolve(CDN_URL + response.files.file);
          } else {
            appContext.tellError("Could not upload attachment");
            reject("Could not upload attachment");
          }
        },
      );
    });
  }

  async function filePickerCallback(callback, value, meta) {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute(
      "accept",
      meta.filetype === "image" ? "image/*" : "video/*",
    );

    input.onchange = async function () {
      const file = this.files[0];

      await callApi("upload-attachment", { file }).then((response) => {
        if (response.status === 1 && response.files?.file) {
          callback(CDN_URL + response.files.file);
        } else {
          appContext.tellError("Could not upload attachment");
        }
      });
    };

    input.click();
  }

  function startEditing(thread) {
    setEditingThread(thread.uuid);
    setEditTitle(thread.title || "");
    setEditBody(thread.body || "");
  }

  function cancelEditing() {
    setEditingThread(null);
    setEditTitle("");
    setEditBody("");
  }

  async function handleCreateThread(event) {
    event.preventDefault();

    if (!canPost || submitting) {
      return;
    }

    if (!title.trim() || !hasMeaningfulContent(body)) {
      appContext.tellWarning("Please add a title and message body.");
      return;
    }

    setSubmitting(true);
    const response = await appContext.createThread({
      title: title.trim(),
      body: body.trim(),
    });
    setSubmitting(false);

    if (response.status === 1) {
      setTitle("");
      setBody("");
      if (createEditorRef.current?.editor) {
        createEditorRef.current.editor.setContent("");
      }
      appContext.tellMessage("Discussion created successfully.");

      if (response.data?.uuid) {
        appContext.navTo({
          item: "forum",
          subItem: "thread",
          extraItem: response.data.uuid,
        });
      }
    } else {
      appContext.tellError(response.msg || "Could not create the thread.");
    }
  }

  async function handleUpdateThread(uuid) {
    if (!editTitle.trim() || !hasMeaningfulContent(editBody)) {
      appContext.tellWarning("Please fill in both title and body.");
      return;
    }

    const response = await appContext.updateThread({
      uuid,
      title: editTitle.trim(),
      body: editBody.trim(),
    });

    if (response.status === 1) {
      appContext.tellMessage("Thread updated successfully.");
      cancelEditing();
    } else {
      appContext.tellError(response.msg || "Could not update the thread.");
    }
  }

  async function handleDeleteThread(thread) {
    if (!window.confirm(`Delete the thread "${thread.title}"?`)) {
      return;
    }

    const response = await appContext.deleteThread({ uuid: thread.uuid });

    if (response.status === 1) {
      appContext.tellMessage("Thread deleted successfully.");
    } else {
      appContext.tellError(response.msg || "Could not delete the thread.");
    }
  }

  const filteredThreads = useMemo(() => {
    let list = Array.isArray(appContext.allThreads)
      ? appContext.allThreads
      : [];

    if (filter.trim()) {
      const query = filter.trim().toLowerCase();
      list = list.filter((item) => {
        return (
          String(item.title || "")
            .toLowerCase()
            .includes(query) ||
          stripHtml(item.body || "")
            .toLowerCase()
            .includes(query) ||
          String(item.fullName || "")
            .toLowerCase()
            .includes(query)
        );
      });
    }

    return list;
  }, [filter, appContext.allThreads]);

  const myThreadCount = filteredThreads.filter(
    (item) => item.userUuid === appContext.currentUserData?.uuid,
  ).length;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    init();
  }, []);

  if (!ready) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  return (
    <>
      <section
        className="page-hero text-white"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(18,60,140,0.96), rgba(10,31,74,0.9))",
        }}
      >
        <div className="container py-5">
          <div className="row align-items-center g-4">
            <div className="col-lg-8" data-aos="fade-up">
              <p className="about-tag bg-light text-dark d-inline-flex align-items-center mb-3">
                Member discussions
              </p>
              <h1 className="font-bold display-5 mb-3">TEF Community Forum</h1>
              <p className="lead mb-0" style={{ maxWidth: "720px" }}>
                Start thoughtful conversations, ask professional questions, and
                learn from fellow members in a clean discussion space.
              </p>
            </div>

            <div className="col-lg-4" data-aos="fade-left">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "20px" }}
              >
                <div className="card-body p-4 text-dark">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <MdForum className="text-primary" size={24} />
                    <h6 className="mb-0 font-bold">Forum access</h6>
                  </div>

                  <p className="mb-2 text-muted">
                    Logged in as:
                    <strong className="text-dark">
                      {" "}
                      {appContext.currentUserData?.email || "Guest"}
                    </strong>
                  </p>
                  <p className="mb-0 text-muted">
                    Posting permission:
                    <strong
                      className={canPost ? "text-success" : "text-warning"}
                    >
                      {canPost ? " Enabled" : " Restricted"}
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="MainBodySection py-5 bg-background">
        <div className="container">
          <div className="row g-4 mb-4">
            <div className="col-md-4">
              <div className="newsletter-modal-card membership-panel-card h-100">
                <span className="newsletter-modal-badge">Discussions</span>
                <h3 className="font-bold text-primary mt-3 mb-1">
                  {appContext.allThreads?.length || 0}
                </h3>
                <p className="mb-0 text-muted">Total active threads</p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="newsletter-modal-card membership-panel-card h-100">
                <span className="newsletter-modal-badge">Your activity</span>
                <h3 className="font-bold text-primary mt-3 mb-1">
                  {myThreadCount}
                </h3>
                <p className="mb-0 text-muted">Threads matching your account</p>
              </div>
            </div>

            <div className="col-md-4">
              <div className="newsletter-modal-card membership-panel-card h-100">
                <span className="newsletter-modal-badge">Status</span>
                <h3
                  className={`font-bold mt-3 mb-1 ${canPost ? "text-success" : "text-warning"}`}
                >
                  {canPost
                    ? "Registered"
                    : appContext.currentUserData?.status || "Login required"}
                </h3>
                <p className="mb-0 text-muted">
                  Only registered members can create threads and replies.
                </p>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-lg-4" data-aos="fade-up">
              {!appContext.auth ? (
                <div className="newsletter-modal-card membership-panel-card h-100">
                  <span className="newsletter-modal-badge">Members only</span>
                  <h5 className="font-bold text-primary mt-3 mb-2">
                    Sign in to access the forum
                  </h5>
                  <p className="text-muted mb-4">
                    Visit the membership portal and log in to join discussions.
                  </p>
                  <button
                    className="btn btn-primary btn-padded"
                    onClick={() => appContext.navTo({ item: "membership" })}
                  >
                    Open membership portal
                  </button>
                </div>
              ) : canPost ? (
                <div className="newsletter-modal-card membership-panel-card h-100">
                  <span className="newsletter-modal-badge">Start a thread</span>
                  <h5 className="font-bold text-primary mt-3 mb-2">
                    Share a new discussion
                  </h5>
                  <p className="text-muted mb-4">
                    Keep the topic relevant, respectful, and useful to other TEF
                    members.
                  </p>

                  <form onSubmit={handleCreateThread}>
                    <div className="mb-3">
                      <label className="mb-2">Thread title</label>
                      <input
                        type="text"
                        className="form-control newsletter-modal-input"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="What would you like to discuss?"
                        maxLength={180}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="mb-2">Thread body</label>
                      <Editor
                        tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js"
                        initialValue="<p></p>"
                        ref={createEditorRef}
                        init={{
                          height: 320,
                          menubar: false,
                          branding: false,
                          plugins: [
                            "advlist",
                            "autolink",
                            "lists",
                            "link",
                            "image",
                            "charmap",
                            "preview",
                            "anchor",
                            "searchreplace",
                            "visualblocks",
                            "code",
                            "fullscreen",
                            "insertdatetime",
                            "media",
                            "table",
                            "help",
                            "wordcount",
                          ],
                          toolbar:
                            "undo redo | formatselect | bold italic backcolor | " +
                            "alignleft aligncenter alignright alignjustify | " +
                            "bullist numlist outdent indent | removeformat | image media | help",
                          images_upload_handler: imageUploadHandler,
                          file_picker_callback: filePickerCallback,
                          image_title: true,
                          automatic_uploads: true,
                        }}
                        onEditorChange={(content) => setBody(content)}
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary btn-padded w-100"
                      disabled={submitting}
                    >
                      <MdAdd className="me-2" />
                      {submitting ? "Creating..." : "Create discussion"}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="newsletter-modal-card membership-panel-card h-100">
                  <span className="newsletter-modal-badge">
                    Posting restricted
                  </span>
                  <h5 className="font-bold text-primary mt-3 mb-2">
                    Read-only access for now
                  </h5>
                  <p className="text-muted mb-0">
                    Your account status is currently
                    <strong>
                      {" "}
                      {appContext.currentUserData?.status || "pending"}
                    </strong>
                    . Once it becomes <strong>registered</strong>, you will be
                    able to create threads and replies.
                  </p>
                </div>
              )}
            </div>

            <div className="col-lg-8" data-aos="fade-up" data-aos-delay="80">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
                <div>
                  <h4 className="font-bold text-primary mb-1">
                    Recent discussions
                  </h4>
                  <p className="text-muted mb-0">
                    Browse active conversations from TEF members.
                  </p>
                </div>

                <div className="input-group" style={{ maxWidth: "320px" }}>
                  <span className="input-group-text bg-white border-end-0">
                    <MdSearch />
                  </span>
                  <input
                    className="form-control border-start-0"
                    placeholder="Search discussions"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                  />
                </div>
              </div>

              {filteredThreads.length > 0 ? (
                filteredThreads.map((thread, index) => {
                  const isOwner =
                    thread.userUuid === appContext.currentUserData?.uuid;

                  return (
                    <div
                      key={thread.uuid || index}
                      className="card border-0 shadow-sm mb-3"
                      style={{ borderRadius: "20px" }}
                    >
                      <div className="card-body p-4">
                        {editingThread === thread.uuid ? (
                          <div>
                            <div className="mb-3">
                              <label className="mb-2">Title</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editTitle}
                                onChange={(event) =>
                                  setEditTitle(event.target.value)
                                }
                              />
                            </div>

                            <div className="mb-3">
                              <label className="mb-2">Body</label>
                              <Editor
                                key={`edit-thread-${thread.uuid}`}
                                tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@6/tinymce.min.js"
                                initialValue={editBody || "<p></p>"}
                                init={{
                                  height: 260,
                                  menubar: false,
                                  branding: false,
                                  plugins: [
                                    "advlist",
                                    "autolink",
                                    "lists",
                                    "link",
                                    "image",
                                    "charmap",
                                    "preview",
                                    "anchor",
                                    "searchreplace",
                                    "visualblocks",
                                    "code",
                                    "fullscreen",
                                    "insertdatetime",
                                    "media",
                                    "table",
                                    "help",
                                    "wordcount",
                                  ],
                                  toolbar:
                                    "undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist | image media | help",
                                  images_upload_handler: imageUploadHandler,
                                  file_picker_callback: filePickerCallback,
                                  image_title: true,
                                  automatic_uploads: true,
                                }}
                                onEditorChange={(content) => setEditBody(content)}
                              />
                            </div>

                            <div className="d-flex gap-2 flex-wrap">
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleUpdateThread(thread.uuid)}
                              >
                                Save changes
                              </button>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={cancelEditing}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-3">
                              <div>
                                <div className="d-flex flex-wrap gap-2 mb-2">
                                  <span className="badge bg-light text-primary border">
                                    Discussion
                                  </span>
                                  {isOwner ? (
                                    <span className="badge bg-secondary">
                                      Your thread
                                    </span>
                                  ) : null}
                                </div>

                                <h5 className="font-bold text-primary mb-2">
                                  {thread.title}
                                </h5>
                                <p
                                  className="text-muted mb-0"
                                  style={{ whiteSpace: "pre-line" }}
                                >
                                  {shortenText(thread.body)}
                                </p>
                              </div>

                              <div className="d-flex align-items-start">
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() =>
                                    appContext.navTo({
                                      item: "forum",
                                      subItem: "thread",
                                      extraItem: thread.uuid,
                                    })
                                  }
                                >
                                  Open
                                  <MdArrowForward className="ms-1" />
                                </button>
                              </div>
                            </div>

                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                              <div className="text-muted small d-flex flex-wrap gap-3">
                                <span>
                                  <strong>By:</strong>{" "}
                                  {thread.fullName || "TEF Member"}
                                </span>
                                <span>
                                  <strong>Posted:</strong>{" "}
                                  {formatDate(thread.dateCreated)}
                                </span>
                              </div>

                              {isOwner && canPost ? (
                                <div className="d-flex gap-2 flex-wrap">
                                  <button
                                    className="btn btn-light btn-sm border"
                                    onClick={() => startEditing(thread)}
                                  >
                                    <MdEdit className="me-1" />
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-light btn-sm border text-danger"
                                    onClick={() => handleDeleteThread(thread)}
                                  >
                                    <MdDelete className="me-1" />
                                    Delete
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="newsletter-modal-card membership-panel-card text-center">
                  <MdLockOutline className="text-primary mb-3" size={42} />
                  <h5 className="font-bold text-primary">
                    No discussions found
                  </h5>
                  <p className="text-muted mb-0">
                    {filter.trim()
                      ? "Try a different search term."
                      : "Be the first registered member to start a conversation."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
